// ============================================================
// PIE — NÚCLEO: optimize() — algoritmo formal de optimización
// ============================================================
// 1. GENERAR   → todas las strategies registradas (nunca una sola)
// 2. FILTRAR   → invariantes permanentes I1-I4 (no configurables)
// 3. PUNTUAR   → goals ponderados del tenant (determinista, puro)
// 4. ELEGIR    → argmax (empate → menor strategyId, estable)
// 5. EXPLICAR  → rationale top-N + explain completo (P8 — contrato)
//
// Propiedades: determinismo, trazabilidad, resiliencia (fallback =
// vce-baseline si ningún candidato es válido), headless-ready.
// ============================================================
import type {
  DecisionExplanation,
  PresentationCandidate,
  PresentationContext,
  PresentationDecision,
  PresentationIntent,
  RationaleEntry,
  SubjectFacts,
  TenantPieConfig,
} from './types';
import { filterAuthentic } from './invariants';
import { GOAL_IMPLEMENTATIONS } from './goals';
import { STRATEGY_REGISTRY } from './strategies';

const FALLBACK_STRATEGY_ID = 'vce-baseline';

/** Candidato con candidateId asignado (trazabilidad). */
type CandidateWithId = PresentationCandidate & { candidateId: string };

/** Los candidatos generados ya llevan candidateId; el filtro los preserva por referencia. */
function asWithId(c: PresentationCandidate): CandidateWithId {
  return c as CandidateWithId;
}

export interface OptimizeOptions {
  strategyRegistry?: Record<string, import('./types').Strategy>;
  goalRegistry?: Record<string, import('./types').Goal>;
  /** Analyzers a ejecutar ANTES de optimizar (enriquecen facts). */
  analyzers?: (ctx: import('./types').AnalyzeContext) => Promise<Partial<SubjectFacts>>;
  analyzerCtx?: import('./types').AnalyzeContext;
}

/** Ejecuta la pipeline completa: (facts opcionalmente enriquecidos) → decisión. */
export async function optimize(
  facts: SubjectFacts,
  context: PresentationContext,
  intent: PresentationIntent,
  config: TenantPieConfig,
  options: OptimizeOptions = {},
): Promise<PresentationDecision> {
  // 0. Enriquecer facts con analyzers (si se proveen)
  let effectiveFacts = facts;
  if (options.analyzers && options.analyzerCtx) {
    const extra = await options.analyzers(options.analyzerCtx);
    effectiveFacts = { ...facts, ...extra };
  }

  const strategies = options.strategyRegistry ?? STRATEGY_REGISTRY;
  const goals = options.goalRegistry ?? GOAL_IMPLEMENTATIONS;
  const activeGoals = config.goals.filter(g => goals[g.id]);

  // 1. GENERAR — todas las strategies (nunca una sola)
  const candidates: PresentationCandidate[] = Object.values(strategies).flatMap(s =>
    s.generate(effectiveFacts, context, intent, config),
  );
  // candidateId estable para trazabilidad
  const withIds = candidates.map((c, i) => ({ ...c, candidateId: `${c.strategyId}#${i}` }));

  // 2. FILTRAR — invariantes I1-I4
  const { valid, rejected } = filterAuthentic(withIds, effectiveFacts);

  // 3. PUNTUAR — goals ponderados (determinista)
  const scored = valid.map(c => {
    const cc = asWithId(c);
    const breakdown = activeGoals.map(g => ({
      goal: g.id,
      weight: g.weight,
      score: goals[g.id].score(cc, effectiveFacts, config),
    }));
    const total = breakdown.reduce((sum, b) => sum + b.weight * b.score, 0);
    return { candidate: cc, total, breakdown };
  });

  // 4. ELEGIR — argmax con desempate estable (menor strategyId)
  // Desempate determinista: el baseline gana el empate (piso anti-regresión),
  // luego menor strategyId (estable). Sin aleatoriedad.
  const tieBreak = (a: CandidateWithId, b: CandidateWithId) => {
    const aBase = a.strategyId === FALLBACK_STRATEGY_ID ? 1 : 0;
    const bBase = b.strategyId === FALLBACK_STRATEGY_ID ? 1 : 0;
    if (aBase !== bBase) return bBase - aBase;
    return a.strategyId.localeCompare(b.strategyId);
  };
  let best = scored.sort((a, b) =>
    b.total - a.total || tieBreak(a.candidate, b.candidate),
  )[0];

  // P9 — EVOLUTION WITHOUT REGRESSION: la ganadora nunca puede ser PEOR que el
  // baseline. Si el argmax no es baseline y puntúa menos que el mejor baseline
  // (no debería ocurrir con argmax, pero la guardia es la garantía contractual),
  // se restaura el baseline. La mejora queda demostrada en explain.regressionGuard.
  const baselineCandidates = scored.filter(s => s.candidate.strategyId === FALLBACK_STRATEGY_ID);
  if (baselineCandidates.length > 0) {
    const bestBaseline = baselineCandidates.sort((a, b) => b.total - a.total)[0];
    if (best.candidate.strategyId !== FALLBACK_STRATEGY_ID && best.total < bestBaseline.total) {
      best = bestBaseline;
    }
  }

  // Resiliencia: sin candidatos válidos → fallback vce-baseline
  if (!best) {
    const fb = strategies[FALLBACK_STRATEGY_ID];
    if (fb) {
      const fbCandidates = fb.generate(effectiveFacts, context, intent, config).map((c, i) => ({
        ...c, candidateId: `${c.strategyId}#${i}`,
      }));
      const fbValid = filterAuthentic(fbCandidates, effectiveFacts).valid;
      if (fbValid.length > 0) {
        best = { candidate: asWithId(fbValid[0]), total: 0, breakdown: [] };
      }
    }
  }
  if (!best) {
    // Último recurso: candidato mínimo válido (no puede ocurrir con el registry base)
    throw new Error('PIE: ningún candidato válido y sin fallback disponible');
  }

  // 5. EXPLICAR (P8) — rationale top-N + explicación completa
  const rationale: RationaleEntry[] = scored
    .sort((a, b) => b.total - a.total)
    .slice(0, 3)
    .map(s => ({
      candidateId: s.candidate.candidateId,
      strategyId: s.candidate.strategyId,
      total: Number(s.total.toFixed(4)),
      breakdown: s.breakdown.map(b => ({ goal: b.goal, weight: b.weight, score: Number(b.score.toFixed(4)) })),
    }));

  const explain: DecisionExplanation = buildExplanation(
    effectiveFacts, activeGoals.map(g => g.id), scored,
    rejected.map(r => ({ candidate: asWithId(r.candidate), violations: r.violations })),
    best,
  );

  return {
    ...best.candidate,
    rationale,
    explain,
    contractVersion: 1,
  };
}

/** Construye la explicación COMPLETA (P8) de la decisión. */
function buildExplanation(
  facts: SubjectFacts,
  goalIds: string[],
  scored: Array<{ candidate: PresentationCandidate & { candidateId: string }; total: number; breakdown: Array<{ goal: string; weight: number; score: number }> }>,
  rejected: Array<{ candidate: PresentationCandidate & { candidateId: string }; violations: string[] }>,
  best: { candidate: PresentationCandidate & { candidateId: string }; total: number; breakdown: Array<{ goal: string; weight: number; score: number }> },
): DecisionExplanation {
  // facts USADOS = los que realmente influyeron (no todos los disponibles)
  const factsUsed: DecisionExplanation['factsUsed'] = [];
  const interesting: Array<keyof SubjectFacts> = [
    'realAspect', 'subjectBox', 'subjectCoverage', 'bgUniformity', 'hasLetterbox',
    'dominantColors', 'infoWeight', 'state', 'mediaCount', 'hasVideo', 'orientation',
  ];
  for (const k of interesting) {
    const v = facts[k];
    if (v !== undefined) factsUsed.push({ fact: k, value: v });
  }

  const goalsEvaluated: DecisionExplanation['goalsEvaluated'] = best.breakdown.map(b => ({
    goal: b.goal,
    weight: b.weight,
    score: Number(b.score.toFixed(4)),
    perceptiveReading: perceptiveReading(b.goal, b.score),
  }));

  const strategiesConsidered: DecisionExplanation['strategiesConsidered'] = [
    ...rejected.map(r => ({
      strategyId: r.candidate.strategyId,
      candidateId: r.candidate.candidateId,
      valid: false,
      rejectedReason: 'invariant' as const,
      invariantViolation: r.violations.join('; '),
    })),
    ...scored.map(s => ({
      strategyId: s.candidate.strategyId,
      candidateId: s.candidate.candidateId,
      valid: true,
      rejectedReason: (s.candidate.candidateId === best.candidate.candidateId ? 'none' : 'score') as 'none' | 'score',
      totalScore: Number(s.total.toFixed(4)),
    })),
  ];

  const winner = {
    strategyId: best.candidate.strategyId,
    candidateId: best.candidate.candidateId,
    totalScore: Number(best.total.toFixed(4)),
    reason: winnerReason(best, goalIds),
  };

  // P9 — EVOLUTION WITHOUT REGRESSION: evidencia objetiva de mejora/igualdad
  const baselineScores = scored
    .filter(s => s.candidate.strategyId === FALLBACK_STRATEGY_ID)
    .map(s => s.total);
  const baselineScore = baselineScores.length > 0 ? Math.max(...baselineScores) : 0;
  const regressionGuard = {
    baselineStrategyId: FALLBACK_STRATEGY_ID,
    baselineScore: Number(baselineScore.toFixed(4)),
    winnerScore: Number(best.total.toFixed(4)),
    improved: best.total >= baselineScore,
    baselineConsidered: baselineScores.length > 0,
    note: baselineScores.length === 0
      ? 'baseline no disponible en esta ejecución (registry sin vce-baseline)'
      : best.candidate.strategyId === FALLBACK_STRATEGY_ID
        ? 'ganó el baseline (ninguna strategy lo superó)'
        : `la strategy ${best.candidate.strategyId} mejora o iguala al baseline (${best.total.toFixed(4)} >= ${baselineScore.toFixed(4)})`,
  };

  return { factsUsed, goalsEvaluated, strategiesConsidered, winner, regressionGuard };
}

/** Lectura perceptiva de un goal (P4 — armonía humana, no solo número). */
function perceptiveReading(goalId: string, score: number): string {
  if (score >= 0.8) {
    switch (goalId) {
      case 'harmony': return 'fondo coherente, encuadre limpio';
      case 'subject': return 'el sujeto domina el frame';
      case 'consistency': return 'dentro del canon del catálogo';
      case 'breathing': return 'respiración adecuada alrededor del sujeto';
      case 'space': return 'espacio aprovechado, sin letterbox sucio';
      case 'legibility': return 'información legible';
      default: return 'excelente';
    }
  }
  if (score >= 0.5) {
    switch (goalId) {
      case 'harmony': return 'composición aceptable';
      case 'subject': return 'el sujeto es visible pero no domina';
      case 'consistency': return 'cerca del canon, con desviación leve';
      case 'breathing': return 'respiración algo justa o excesiva';
      case 'space': return 'algo de espacio degradante';
      case 'legibility': return 'información aceptable';
      default: return 'aceptable';
    }
  }
  return 'deficiente para este objetivo';
}

/** Razón legible de por qué ganó la estrategia ganadora. */
function winnerReason(
  best: { candidate: PresentationCandidate & { candidateId: string }; total: number; breakdown: Array<{ goal: string; weight: number; score: number }> },
  goalIds: string[],
): string {
  const top = [...best.breakdown].sort((a, b) => b.weight * b.score - a.weight * a.score)[0];
  const parts = [
    `estrategia ${best.candidate.strategyId} con score total ${best.total.toFixed(4)}`,
    `mejor aporte: ${top?.goal} (peso ${top?.weight}, score ${top?.score.toFixed(2)})`,
    `evaluó ${goalIds.length} goals: ${goalIds.join(', ')}`,
  ];
  return parts.join(' — ');
}

/** Versión síncrona del núcleo (para tests y uso sin analyzers async). */
export function optimizeSync(
  facts: SubjectFacts,
  context: PresentationContext,
  intent: PresentationIntent,
  config: TenantPieConfig,
  options: Omit<OptimizeOptions, 'analyzers' | 'analyzerCtx'> = {},
): PresentationDecision {
  const strategies = options.strategyRegistry ?? STRATEGY_REGISTRY;
  const goals = options.goalRegistry ?? GOAL_IMPLEMENTATIONS;
  const activeGoals = config.goals.filter(g => goals[g.id]);

  const candidates: PresentationCandidate[] = Object.values(strategies).flatMap(s =>
    s.generate(facts, context, intent, config),
  );
  const withIds = candidates.map((c, i) => ({ ...c, candidateId: `${c.strategyId}#${i}` }));
  const { valid, rejected } = filterAuthentic(withIds, facts);

  const scored = valid.map(c => {
    const cc = asWithId(c);
    const breakdown = activeGoals.map(g => ({
      goal: g.id, weight: g.weight, score: goals[g.id].score(cc, facts, config),
    }));
    const total = breakdown.reduce((sum, b) => sum + b.weight * b.score, 0);
    return { candidate: cc, total, breakdown };
  });

  // Desempate determinista: el baseline gana el empate (piso anti-regresión),
  // luego menor strategyId (estable). Sin aleatoriedad.
  const tieBreak = (a: CandidateWithId, b: CandidateWithId) => {
    const aBase = a.strategyId === FALLBACK_STRATEGY_ID ? 1 : 0;
    const bBase = b.strategyId === FALLBACK_STRATEGY_ID ? 1 : 0;
    if (aBase !== bBase) return bBase - aBase;
    return a.strategyId.localeCompare(b.strategyId);
  };
  let best = scored.sort((a, b) =>
    b.total - a.total || tieBreak(a.candidate, b.candidate),
  )[0];

  if (!best) {
    const fb = strategies[FALLBACK_STRATEGY_ID];
    if (fb) {
      const fbCandidates = fb.generate(facts, context, intent, config).map((c, i) => ({
        ...c, candidateId: `${c.strategyId}#${i}`,
      }));
      const fbValid = filterAuthentic(fbCandidates, facts).valid;
      if (fbValid.length > 0) best = { candidate: asWithId(fbValid[0]), total: 0, breakdown: [] };
    }
  }
  if (!best) throw new Error('PIE: ningún candidato válido y sin fallback disponible');

  const rationale: RationaleEntry[] = scored
    .sort((a, b) => b.total - a.total)
    .slice(0, 3)
    .map(s => ({
      candidateId: s.candidate.candidateId,
      strategyId: s.candidate.strategyId,
      total: Number(s.total.toFixed(4)),
      breakdown: s.breakdown.map(b => ({ goal: b.goal, weight: b.weight, score: Number(b.score.toFixed(4)) })),
    }));

  const explain = buildExplanation(
    facts, activeGoals.map(g => g.id), scored,
    rejected.map(r => ({ candidate: asWithId(r.candidate), violations: r.violations })),
    best,
  );

  return { ...best.candidate, rationale, explain, contractVersion: 1 };
}
